import Keycloak from 'keycloak-js';

// Keycloak configuration
export const keycloak = new Keycloak({
  url: 'https://sso.sudosys.org',
  realm: 'sudosys',
  clientId: 'ui',
});

let initPromise: Promise<boolean> | null = null;

// Initialize Keycloak
export const initKeycloak = async (): Promise<boolean> => {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      const authenticated = await keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html',
        pkceMethod: 'S256',
        checkLoginIframe: false, // Disable for security reasons
      });
      return authenticated;
    } catch (error) {
      // Check if it's the "already initialized" error, which we can ignore
      if (
        error instanceof Error &&
        error.message.includes('initialized once')
      ) {
        return keycloak.authenticated || false;
      }
      console.error('[Keycloak] Initialization error:', error);
      initPromise = null; // Allow retrying on actual errors
      return false;
    }
  })();

  return initPromise;
};

// Authentication methods
export const login = (redirectPath?: string) => {
  const redirectUri = redirectPath
    ? window.location.origin + redirectPath
    : window.location.origin;

  keycloak.login({
    redirectUri,
  });
};

export const logout = () => {
  keycloak.logout({
    redirectUri: window.location.origin,
  });
};

export const register = () => {
  keycloak.register({
    redirectUri: window.location.origin,
  });
};

// Token management
export const getToken = () => keycloak.token;

export const getRefreshToken = () => keycloak.refreshToken;

export const isTokenExpired = () => keycloak.isTokenExpired();

export const updateToken = (minValidity: number = 30) => {
  return keycloak.updateToken(minValidity);
};

// User information
export const getUserInfo = () => {
  if (keycloak.tokenParsed) {
    return {
      id: keycloak.tokenParsed.sub,
      username: keycloak.tokenParsed.preferred_username,
      email: keycloak.tokenParsed.email,
      firstName: keycloak.tokenParsed.given_name,
      lastName: keycloak.tokenParsed.family_name,
      roles: keycloak.tokenParsed.realm_access?.roles || [],
    };
  }
  return null;
};

// Role-based access control
export const hasRole = (role: string): boolean => {
  return keycloak.hasRealmRole(role);
};

export const hasAnyRole = (roles: string[]): boolean => {
  return roles.some(role => keycloak.hasRealmRole(role));
};

export const hasAllRoles = (roles: string[]): boolean => {
  return roles.every(role => keycloak.hasRealmRole(role));
};

// Resource-based access control
export const hasResourceRole = (resource: string, role: string): boolean => {
  return keycloak.hasResourceRole(role, resource);
};
